(() => {
  if (customElements.get("brand-values")) return;

  class BrandValues extends HTMLElement {
    constructor() {
      super();
      this.cleanup = [];
      this.activeId = null;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
    }

    connectedCallback() {
      this.sectionId =
        this.closest(".shopify-section")?.dataset?.sectionId || null;
      this.images = Array.from(this.querySelectorAll("[data-value-image]"));
      this.items = Array.from(this.querySelectorAll("[data-value-item]"));
      this.canHover =
        window.matchMedia &&
        window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      this.bindEvents();
      this.setDefaultActive();
      this.bindEditorEvents();
    }

    disconnectedCallback() {
      this.cleanup.forEach((remove) => remove());
      this.cleanup = [];
    }

    addListener(el, event, handler, options) {
      if (!el) return;
      el.addEventListener(event, handler, options);
      this.cleanup.push(() => el.removeEventListener(event, handler, options));
    }

    setDefaultActive() {
      const defaultItem = this.items.find((item) =>
        item.hasAttribute("data-default-active")
      );
      const id =
        (defaultItem && defaultItem.dataset.valueItem) ||
        (this.items[0] && this.items[0].dataset.valueItem);
      if (id) this.setActive(id);
    }

    setActive(id) {
      if (!id || id === this.activeId) return;
      this.activeId = id;

      this.images.forEach((image) => {
        const isActive = image.dataset.valueImage === id;
        image.classList.toggle("is-active", isActive);
        image.classList.toggle("opacity-100", isActive);
        image.classList.toggle("z-10", isActive);
        image.classList.toggle("scale-100", isActive);
        image.classList.toggle("opacity-0", !isActive);
        image.classList.toggle("z-0", !isActive);
        image.classList.toggle("scale-110", !isActive);
      });

      this.items.forEach((item) => {
        const isActive = item.dataset.valueItem === id;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-expanded", String(isActive));
        const title = item.querySelector("[data-value-title]");
        const detail = item.querySelector("[data-value-detail]");
        if (title) {
          title.classList.toggle("text-[rgb(var(--color-foreground))]", isActive);
          title.classList.toggle(
            "text-[rgba(var(--color-foreground),0.3)]",
            !isActive
          );
        }
        if (detail) {
          detail.classList.toggle("max-h-40", isActive);
          detail.classList.toggle("opacity-100", isActive);
          detail.classList.toggle("mt-6", isActive);
          detail.classList.toggle("max-h-0", !isActive);
          detail.classList.toggle("opacity-0", !isActive);
          if (!isActive) {
            detail.classList.remove("mt-6");
          }
        }
      });
    }

    bindEvents() {
      const activateFromEvent = (event) => {
        const id = event.currentTarget.dataset.valueItem;
        if (id) this.setActive(id);
      };

      const hoverEvent = this.canHover ? "mouseenter" : null;
      if (hoverEvent) {
        this.items.forEach((item) =>
          this.addListener(item, hoverEvent, activateFromEvent)
        );
      }

      this.items.forEach((item) => {
        this.addListener(item, "click", activateFromEvent);
        this.addListener(item, "keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            activateFromEvent(event);
          }
        });
      });
    }

    bindEditorEvents() {
      this.addListener(document, "shopify:block:select", this.onBlockSelect);
      this.addListener(document, "shopify:section:select", this.onSectionSelect);
      this.addListener(window, "shopify:block:select", this.onBlockSelect);
      this.addListener(window, "shopify:section:select", this.onSectionSelect);
    }

    onBlockSelect(event) {
      const blockId = event?.detail?.blockId;
      const sectionId = event?.detail?.sectionId;
      if (this.sectionId && sectionId && this.sectionId !== sectionId) return;
      if (!blockId) return;
      const hasBlock = this.items.some(
        (item) => item.dataset.valueItem === blockId
      );
      if (hasBlock) {
        this.setActive(blockId);
      }
    }

    onSectionSelect(event) {
      const sectionId = event?.detail?.sectionId;
      if (!sectionId) return;
      if (this.sectionId && this.sectionId === sectionId) {
        this.setDefaultActive();
      }
    }
  }

  customElements.define("brand-values", BrandValues);
})();
